import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';

import {
    IWizardOptions,
    ITaskMetaData,
    IDynamicVisibility,
    IWizardContact,
    IWizardOptionsForPayload,
    IWizardResponseData
} from '../../models';

import {
    IContactSchema,
    IWizardTaskMeeting,
    IWizardConfig,
    IWizardUser,
    UserPlan,
    IDropdown,
    IDefaultWizardOptions
} from 'common-lib';

interface IQuickDates {
    label: string;
    start: moment.Moment;
    end: moment.Moment;
}

import moment_ from 'moment-timezone';
const moment = moment_;
import * as _ from 'lodash';

/* ---------------------------------------------------------- */
/* -- This service is a Store for holding the Wizard state -- */
/* ---------------------------------------------------------- */
@Injectable()
export class WizardService {
    /* We set the value of these BehaviorSubjects after init, therefore we do not type them with initial values */

    private readonly _options = new BehaviorSubject<IWizardOptions>({});
    readonly options$ = this._options.asObservable();

    /**
     * For skills with existing task e.g. rescheduler, the default values for components do not come from this but from this.options
     * These components depend on the response from requestDefaultOptions: Duration, TimePicker, DateRange, Subject, Room
     * */
    private readonly _onNewDefaultOptions = new Subject<IWizardOptions>();
    readonly onNewDefaultOptions$ = this._onNewDefaultOptions.asObservable();

    private readonly _meetingType = new BehaviorSubject<string>('meeting');
    readonly meetingType$ = this._meetingType.asObservable();

    private readonly _selection = new BehaviorSubject<IWizardContact[]>([]);
    readonly selection$ = this._selection.asObservable();

    private readonly _quickDates = new BehaviorSubject<IQuickDates | null>(null);
    readonly quickDates$ = this._quickDates.asObservable();

    private readonly _responseData = new BehaviorSubject<IWizardResponseData>({});
    readonly responseData$ = this._responseData.asObservable();

    private readonly _taskMetaData = new BehaviorSubject<ITaskMetaData>(null);
    readonly taskMetaData$ = this._taskMetaData.asObservable();

    private readonly _dynamicVisibility = new BehaviorSubject<IDynamicVisibility>({ componentsToHide: [] });
    readonly dynamicVisibility$ = this._dynamicVisibility.asObservable();

    private initialMeetingData?: IWizardOptions;

    /** Static Wizard values */
    public dataCache: any = null; // stores current Wizard data/options in memory e.g. used in the Suggestions component
    public existingContacts: IWizardContact[] = []; // keep reference to existing invitees for Change Attendees skill
    public vipContacts: IWizardContact[] = [];
    public customSubject: string = '';
    public isCustomSubject: boolean = false;
    public isCustomLocation: boolean = false;
    public meetingTypes: { meeting: IDropdown[]; class: IDropdown[] } = {
        meeting: [],
        class: []
    };
    private user: IWizardUser = {
        name: '',
        avatar: '',
        plan: UserPlan.Basic,
        timezone: 'America/Toronto'
    };

    public config: IWizardConfig = {
        skill: 'meeting-create',
        isPlugin: false
    };

    constructor() {}

    // ================= //
    // Getters / Setters //
    // ================= //

    get options() {
        return this._options.getValue();
    }

    set options(options: IWizardOptions) {
        this._options.next(options);
    }

    updateOptions(options: IWizardOptions) {
        if (Object.keys(options).length > 1) {
            this.options = { ...this.options, ...(options as IWizardOptions) };
        } else {
            /** GOTCHA: Updating single-value options this way will NOT emit events to options$ subscribers */
            const key = Object.keys(options)[0];
            const value = options[key];
            this.options[key] = value;
        }
    }

    get meetingType() {
        return this._meetingType.getValue();
    }

    set meetingType(type: string) {
        this._meetingType.next(type);
    }

    updateMeetingType(type: string, isClass?: boolean) {
        this.updateOptions({ type: type });
        this.meetingType = type;

        if (isClass) {
            this.meetingTypes.class.forEach((classType) => (classType.selected = classType.value === type));
        } else {
            this.meetingTypes.meeting.forEach((meetingType) => (meetingType.selected = meetingType.value === type));
        }
    }

    set defaultOptions(defaults: IWizardOptions) {
        this._onNewDefaultOptions.next(defaults);
    }

    updateDefaultOptions(data: IDefaultWizardOptions) {
        const transformedOptions = this.transformDataForWizardOptions(data);
        this.defaultOptions = { ...this.defaultOptions, ...transformedOptions };
    }

    get selection(): IWizardContact[] {
        return this._selection.getValue();
    }

    set selection(selection: IWizardContact[]) {
        this._selection.next(selection);
    }

    get initialMeeting() {
        return this.initialMeetingData;
    }

    /**
     * @params contact: the contact object that comes back from /contact endpoint
     * @params shouldDeleteSubject: contacts are tied to meeting subject because the default title is comprised of contact names
     */
    updateSelection(contact: IContactSchema, options: { shouldDeleteSubject: boolean }) {
        let transformedContact = this.transformContactSchemaIn(contact as IContactSchema);

        this.selection = [...this.selection, transformedContact];
        this.enableSubjectUpdate(options.shouldDeleteSubject);

        return transformedContact;
    }

    removeSelection(id: string, shouldDeleteSubject = true) {
        this.selection = this.selection.filter((selected) => {
            return selected.id !== id;
        });
        this.enableSubjectUpdate(shouldDeleteSubject);
    }

    transformSelection(id: string, transformObject: { [key: string]: any }) {
        const selectedItem = _.find(this.selection, { id });

        if (selectedItem) {
            this.selection = _.map(this.selection, (selected) => {
                return selected.id === id ? { ...selected, ...transformObject } : selected;
            });
        }
    }

    setSelection(contacts: IWizardContact[]) {
        this.selection = contacts;
    }

    sortItemToStartOfSelection(id: string) {
        const selectedItem = _.find(this.selection, { id });

        if (selectedItem) {
            this.selection = _.sortBy(this.selection, (selected) => {
                return selected.id === selectedItem.id ? -1 : 1;
            });
        }
    }

    get quickDates() {
        return this._quickDates.getValue();
    }

    set quickDates(interval: IQuickDates | null) {
        this._quickDates.next(interval);
    }

    updateQuickDates(interval: IQuickDates | null) {
        this.quickDates = interval;
    }

    get responseData(): IWizardResponseData {
        return this._responseData.getValue();
    }

    set responseData(data: IWizardResponseData) {
        this._responseData.next(data);
    }

    /**
     * When the response has no 'mode' field, we want to explicitly set it to be 'undefined'.
     * Used in ConfirmationComponent
     */
    updateResponseData(responseData: IWizardResponseData) {
        if (!responseData.mode) responseData.mode = undefined;
        this.responseData = { ...this.responseData, ...responseData };
    }

    resetResponseData() {
        this.responseData = {};
    }

    get taskMetaData(): ITaskMetaData {
        return this._taskMetaData.getValue();
    }

    set taskMetaData(taskMetaData: ITaskMetaData) {
        this._taskMetaData.next(taskMetaData);
    }

    updateTaskMetaData(taskMetaData: ITaskMetaData) {
        this.taskMetaData = taskMetaData;
    }

    updateDataCache(data: any) {
        this.dataCache = { ...this.dataCache, ...data };
    }

    updateDynamicVisibility(value: IDynamicVisibility) {
        this._dynamicVisibility.next(value);
    }

    get currentUser(): IWizardUser {
        return this.user;
    }

    set currentUser(user) {
        this.user = user;
    }

    public saveMeetingAsWizardOptions(meeting: IDefaultWizardOptions) {
        const transformedOptions = this.transformDataForWizardOptions(meeting);
        this.initialMeetingData = transformedOptions;
        this.options = { ...this.options, ...transformedOptions };
        const transformedContacts = _.map(meeting.contacts, (contact) => {
            return this.transformTaskMeetingContactIn(contact);
        });

        this.setSelection(transformedContacts);
    }

    public saveTaskId(taskId: string) {
        this.options = { ...this.options, taskId };
    }

    // ====== //
    // Config //
    // ====== //

    public updateConfig(config: IWizardConfig) {
        this.config = config;
    }

    // TODO: meetingTypes is IAllNamedTypes; we need to move this model to common-lib
    /**
     * This method is only run once during Wizard component initialization
     * The way the wizard is designed, we only know if this is a class type via the JSON data so we must call updateMeetingType from within a Wizard component
     * */

    public setMeetingTypes(meetingTypes: any) {
        const selectedType = (this.meetingType = this.config.selectedMeetingType);
        const meetingStyleTypes = this.mapMeetingTypes(meetingTypes, false, selectedType);
        const classStyleTypes = this.mapMeetingTypes(meetingTypes, true, selectedType);

        this.meetingTypes = {
            meeting: meetingStyleTypes,
            class: classStyleTypes
        };
    }

    // TODO: meetingTypes is IAllNamedTypes
    private mapMeetingTypes(meetingTypes, isClass: boolean, selectedMeetingType: string) {
        const legacyMeetingTypes = ['call', 'coffee', 'breakfast', 'lunch', 'dinner', 'drinks'];

        const filteredMeetingTypes = meetingTypes
            .filter((type) => (isClass ? type.style === 'class' : type.style === 'meeting'))
            .map((type) => {
                return type.name.value;
            });

        const customMeetingTypes = filteredMeetingTypes.filter((type) => !legacyMeetingTypes.includes(type));

        const sortOrder = ['meeting', ...customMeetingTypes, ...legacyMeetingTypes];

        return _.uniq(
            sortOrder.filter((type) => {
                return filteredMeetingTypes.includes(type);
            })
        ).map((type) => {
            return {
                name: type,
                value: type,
                selected: type === selectedMeetingType
            };
        });
    }

    // ============== //
    // Helper Methods //
    // ============== //

    /**
     * By setting subject to null, will enable requestDefaultOptions to be called in MeetingTypeComponent (and update the subject)
     * The subject, however, will not change if the user had customized it ie typed something into the subject field
     */
    private enableSubjectUpdate(shouldDeleteSubject: boolean) {
        if (this.options.subject && shouldDeleteSubject) {
            this.updateOptions({ subject: null });
        }
    }

    private addTimesToDates(dateStart: moment.Moment, timeStart: moment.Moment, timezone: string) {
        const mergedDateStart = `${dateStart.format('YYYY-MM-DD')}T${timeStart.format('HH:mm:ss')}`;

        return moment(mergedDateStart).tz(timezone, true);
    }

    /** This handles transforming both default options from server and data from a task meeting */
    private transformDataForWizardOptions(data: IWizardTaskMeeting | IDefaultWizardOptions) {
        const location = (data.locations && data.locations[0] && [{ name: data.locations[0].name }]) || [];

        /** null cases for videoProvider from the backend can either be an object with empty string, or undefined */
        const videoProvider = !data.videoProvider || _.isEmpty(data.videoProvider.id) ? null : data.videoProvider;

        /**
         * data.dateStart/End comes back in user's timezone, we want to keep but IGNORE offset for displaying time
         * eg: machine's locale is -04 but data.date is 13:00+02, we keep 13:00 and display 13:00 (disregard locale)
         * timezones will be added back to date times in this.addTimesToDates()
         */
        const dateStart = data.dateStart ? moment.parseZone(data.dateStart) : moment(data.meetingTime?.start);
        const dateEnd = data.dateEnd ? moment.parseZone(data.dateEnd) : moment(data.meetingTime?.end);

        return {
            subject: data.subject,
            dateStart,
            dateEnd,
            timeStart: dateStart,
            timeEnd: dateEnd,
            locations: location,
            meetingLength: data.meetingLength,
            videoProvider,
            availabilityType: data.availabilityType,
            meetingTime: data.meetingTime || null,
            availabilityWindows: data.availabilityWindows,
            room: { capacity: (data as IDefaultWizardOptions).roomCapacity },
            maxAttendees: data.maxAttendees
        };
    }

    private transformContactSchemaIn(contact: IContactSchema) {
        return {
            id: contact.id,
            email: contact.email,
            providerId: contact.providerId,
            defaultEmail: contact.defaultEmail || undefined,
            name: contact.name || '',
            imageUrl: contact.imageUrl || '',
            required: false
        };
    }

    private transformTaskMeetingContactIn(contact: IWizardContact) {
        return {
            id: contact.id,
            email: contact.email,
            providerId: contact.providerId,
            defaultEmail: contact.defaultEmail,
            name: contact.name,
            imageUrl: '',
            required: contact.required || false
        };
    }

    public mergeCacheIntoOptions() {
        this.options = {
            ...this.options,
            ...this.dataCache
        };
    }

    /** @param payloadKeys list of key-value params the Wizard endpoint expects to receive */
    public processOptionsForPayload(payloadKeys: string[]) {
        /** take care not to modify the original wizard options data */
        const clonedOptions = _.cloneDeep(this.options);
        const userTimezone = this.currentUser.timezone;

        if (clonedOptions.isSpecificTime && clonedOptions.dateStart) {
            const timeStart = clonedOptions.timeStart || moment('09:00', 'HH:mm');
            clonedOptions.dateStart = this.addTimesToDates(clonedOptions.dateStart, timeStart, userTimezone);
            clonedOptions.dateEnd = moment
                .parseZone(clonedOptions.dateStart)
                .add(clonedOptions.meetingLength, 'minutes');
        } else {
            clonedOptions.dateStart = moment(clonedOptions.dateStart).tz(userTimezone, true).startOf('day');
            clonedOptions.dateEnd = moment(clonedOptions.dateEnd).tz(userTimezone, true).endOf('day');
        }

        const payloadOptions: IWizardOptionsForPayload = {
            ...clonedOptions,
            dateStart: clonedOptions.dateStart.format(),
            dateEnd: moment(clonedOptions.dateEnd).tz(userTimezone, true).format()
        };

        /** prune payloadOptions so that only values specified by payloadKeys (which are from the server) are sent */
        const payload: {
            [path in keyof IWizardOptionsForPayload]: IWizardOptionsForPayload[path];
        } = payloadKeys.reduce((payloadObj, key: keyof IWizardOptions) => {
            return payloadOptions[key] ? { ...payloadObj, [key]: payloadOptions[key] } : payloadObj;
        }, {});

        if (this.vipContacts.length) {
            payload.contacts = payload.contacts.map((contact) => {
                return {
                    ...contact,
                    required: Boolean(_.find(this.vipContacts, { id: contact.id }))
                };
            });
        }

        return _.pickBy(payload, _.identity);
    }
}
